import React, { useState, useEffect } from 'react';
import {
  Box,
  Stepper,
  Step,
  StepLabel,
  Typography,
  Card,
  styled,
  CardContent,
  StepConnector,
  stepConnectorClasses,
  keyframes
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import HourglassEmptyIcon from '@mui/icons-material/HourglassEmpty';
import RadioButtonUncheckedIcon from '@mui/icons-material/RadioButtonUnchecked';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import SyncIcon from '@mui/icons-material/Sync';

const steps = ['Initiated', 'Pending', 'Success'];

// Animation keyframes
const pulse = keyframes`
  0% { transform: scale(1); opacity: 1; }
  50% { transform: scale(1.1); opacity: 0.8; }
  100% { transform: scale(1); opacity: 1; }
`;

const rotate = keyframes`
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
`;

const shimmer = keyframes`
  0% { background-position: -200px 0; }
  100% { background-position: calc(200px + 100%) 0; }
`;

const getStepIcon = (step, activeStep, isProcessing) => {
  const iconStyle = { color: '#fff' };

  if (step < activeStep) {
    return <CheckCircleIcon sx={iconStyle} />;
  } else if (step === activeStep) {
    if (isProcessing) {
      return <SyncIcon sx={{ ...iconStyle, animation: `${rotate} 2s linear infinite` }} />;
    }
    return <HourglassEmptyIcon sx={iconStyle} />;
  } else {
    return <RadioButtonUncheckedIcon sx={iconStyle} />;
  }
};

const WhiteConnector = styled(StepConnector)(({ theme }) => ({
  [`&.${stepConnectorClasses.alternativeLabel}`]: {
    top: 10,
  },
  [`&.${stepConnectorClasses.active}`]: {
    [`& .${stepConnectorClasses.line}`]: {
      borderColor: '#fff',
    },
  },
  [`&.${stepConnectorClasses.completed}`]: {
    [`& .${stepConnectorClasses.line}`]: {
      borderColor: '#fff',
    },
  },
  [`& .${stepConnectorClasses.line}`]: {
    borderColor: '#fff',
    borderTopWidth: 2,
    borderRadius: 1,
  },
}));

const StatusProgressCard = ({ activeStep, statusAgent }) => {
  const [isProcessing, setIsProcessing] = useState(false);
  const [processingText, setProcessingText] = useState('');

  // Determine if we're in processing state
  // If no statusAgent or no taskId, we're not processing (show success)
  const isCurrentlyProcessing = statusAgent?.data?.status && 
                               statusAgent?.data?.status !== 'SUCCESS' && 
                               statusAgent?.data?.status !== 'Success';

  useEffect(() => {
    if (isCurrentlyProcessing) {
      setIsProcessing(true);
      const messages = [
        'Initializing agent...',
        'Loading AI models...',
        'Preparing processing pipeline...',
        'Setting up document handlers...',
        'Configuring output formats...',
        'Starting background services...',
        'Establishing connections...',
        'Validating configurations...'
      ];
      
      let messageIndex = 0;
      const interval = setInterval(() => {
        setProcessingText(messages[messageIndex]);
        messageIndex = (messageIndex + 1) % messages.length;
      }, 2000);

      return () => clearInterval(interval);
    } else {
      setIsProcessing(false);
      setProcessingText('');
    }
  }, [isCurrentlyProcessing]);

  const getBackgroundGradient = () => {
    // If no statusAgent or no status, show success (green)
    if (!statusAgent?.data?.status) {
      return 'linear-gradient(135deg, #43e97b 0%, #38f9d7 100%)'; // Green for success
    }
    
    // If status is SUCCESS, show green
    if (statusAgent?.data?.status === 'SUCCESS' || statusAgent?.data?.status === 'Success') {
      return 'linear-gradient(135deg, #43e97b 0%, #38f9d7 100%)'; // Green for success
    }
    
    // If status is PENDING, show pink
    if (statusAgent?.data?.status === 'PENDING' || statusAgent?.data?.status === 'Pending') {
      return 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)'; // Pink to Red for pending
    }
    
    // For any other status, show blue
    return 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)'; // Blue to Purple
  };

  const getStatusText = () => {
    // If no statusAgent or no status, show success
    if (!statusAgent?.data?.status) {
      return 'Agent successfully deployed!';
    }
    
    // If status is SUCCESS, show success
    if (statusAgent?.data?.status === 'SUCCESS' || statusAgent?.data?.status === 'Success') {
      return 'Agent successfully deployed!';
    }
    
    // If status is PENDING, show processing
    if (statusAgent?.data?.status === 'PENDING' || statusAgent?.data?.status === 'Pending') {
      return 'Agent is processing in background';
    }
    
    // For any other status, show initiated
    return 'Agent deployment initiated';
  };

  return (
    <Card
      sx={{
        background: getBackgroundGradient(),
        color: '#fff',
        borderRadius: 3,
        width: 400,
        padding: 2,
        boxShadow: '0 8px 32px rgba(0, 0, 0, 0.15)',
        position: 'relative',
        overflow: 'hidden',
        '&::before': isProcessing ? {
          content: '""',
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'linear-gradient(90deg, transparent, rgba(255,255,255,0.1), transparent)',
          backgroundSize: '200px 100%',
          animation: `${shimmer} 2s infinite`,
        } : {},
      }}
    >
      <CardContent>
        <Box sx={{ width: '100%' }}>
          {/* Status Text */}
          <Box sx={{ textAlign: 'center', mb: 2 }}>
            <Typography 
              variant="h6" 
              sx={{ 
                fontWeight: 'bold', 
                mb: 1,
                animation: isProcessing ? `${pulse} 2s ease-in-out infinite` : 'none'
              }}
            >
              {getStatusText()}
            </Typography>
            {isProcessing && processingText && (
              <Typography 
                variant="body2" 
                sx={{ 
                  opacity: 0.9,
                  fontStyle: 'italic',
                  animation: `${pulse} 3s ease-in-out infinite`
                }}
              >
                {processingText}
              </Typography>
            )}
          </Box>

          <Stepper
            alternativeLabel
            activeStep={activeStep}
            connector={<WhiteConnector />}
          >
            {steps.map((label, index) => (
              <Step key={label}>
                <StepLabel
                  icon={getStepIcon(index, activeStep, isProcessing && index === activeStep)}
                  sx={{
                    '& .MuiStepLabel-label': {
                      color: '#fff',
                      fontWeight: 'bold',
                      fontSize: '14px',
                    },
                    '& .MuiStepLabel-iconContainer': {
                      color: '#fff',
                      animation: isProcessing && index === activeStep ? `${pulse} 1.5s ease-in-out infinite` : 'none',
                    },
                  }}
                >
                  <Typography style={{ fontSize: '14px', fontWeight: 'bold' }} variant="body2">
                    {label}
                  </Typography>
                </StepLabel>
              </Step>
            ))}
          </Stepper>

          {/* Processing Indicator */}
          {isProcessing && (
            <Box sx={{ 
              display: 'flex', 
              alignItems: 'center', 
              justifyContent: 'center', 
              mt: 2,
              gap: 1
            }}>
              <Box sx={{
                width: 8,
                height: 8,
                borderRadius: '50%',
                backgroundColor: '#fff',
                animation: `${pulse} 1s ease-in-out infinite`,
                '&:nth-of-type(2)': {
                  animationDelay: '0.2s',
                },
                '&:nth-of-type(3)': {
                  animationDelay: '0.4s',
                },
              }} />
              <Box sx={{
                width: 8,
                height: 8,
                borderRadius: '50%',
                backgroundColor: '#fff',
                animation: `${pulse} 1s ease-in-out infinite`,
                animationDelay: '0.2s',
              }} />
              <Box sx={{
                width: 8,
                height: 8,
                borderRadius: '50%',
                backgroundColor: '#fff',
                animation: `${pulse} 1s ease-in-out infinite`,
                animationDelay: '0.4s',
              }} />
            </Box>
          )}
        </Box>
      </CardContent>
    </Card>
  );
};

export default StatusProgressCard;
